import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, Badge, Card, CardHeader, CardBody } from '../components/ui';
import { ProfileSkeleton } from '../components/dashboard';

const ProfilePage: React.FC = () => {
  const { user, isLoading } = useAuth0();

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (!user) {
    return (
      <div className="py-6">
        <div className="au-page-header">
          <h1>Profile</h1>
          <p>Unable to load profile information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      {/* Page Header */}
      <div className="au-page-header">
        <h1>My Profile</h1>
        <p>View and manage your account information.</p>
      </div>

      {/* Profile Card */}
      <Card className="mb-6">
        <CardHeader
          title="Account Information"
          subtitle="Your personal details from your identity provider"
          action={
            user.email_verified && (
              <Badge variant="success" dot>
                Verified
              </Badge>
            )
          }
        />
        <CardBody>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar
                size="xl"
                src={user.picture}
                name={user.name || user.email || 'User'}
                status="online"
              />
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                  Full Name
                </label>
                <p className="text-lg text-au-text-primary font-medium">
                  {user.name || 'Not provided'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                  Email Address
                </label>
                <p className="text-au-text-primary">{user.email || 'Not provided'}</p>
              </div>

              {user.nickname && (
                <div>
                  <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                    Nickname
                  </label>
                  <p className="text-au-text-primary">{user.nickname}</p>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Additional Details Card */}
      <Card>
        <CardHeader title="Account Details" subtitle="Additional information about your account" />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {user.updated_at && (
              <div>
                <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                  Last Updated
                </label>
                <p className="text-au-text-secondary">
                  {new Date(user.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                Email Status
              </label>
              <Badge variant={user.email_verified ? 'success' : 'warning'}>
                {user.email_verified ? 'Verified' : 'Not Verified'}
              </Badge>
            </div>

            {user.locale && (
              <div>
                <label className="block text-sm font-medium text-au-text-tertiary mb-1">
                  Locale
                </label>
                <p className="text-au-text-secondary">{user.locale}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ProfilePage;
